'use client';

import { useEffect, useState } from 'react';
import { useQuotation } from '@/app/context/quotation-context';
import { Filter, X } from 'lucide-react';
import ProductCard from '@/app/components/ProductCard';
import SearchBar from '@/app/components/SearchBar';
import { useRouter, useSearchParams } from 'next/navigation';

// Define types for Product and API response
interface Product {
  id: number;
  name: string;
  description: string | null;
  imageUrl?: string | null;
  imageData?: string | null;
  imageMimeType?: string | null;
  category: string | null;
  price: number | null;
  price_ars?: number | null;
  pricingStrategy?: 'single' | 'per_package';
  peso?: string | null;
  unidad?: string | null;
  estado?: string | null;
  specifications?: Record<string, unknown> | unknown;
  isFeatured: boolean;
  isActive: boolean;
  isQuotable?: boolean;
  isSellable?: boolean;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  packages?: PackageWithImage[];
}

interface PackageWithImage {
  id: number;
  productId: number;
  packageType: string;
  weight: string;
  unit: string;
  price: string | null;
  price_ars: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  imagePath?: string | null;
}

interface PaginationData {
  totalItems: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface ProductsResponse {
  data: Product[];
  pagination: PaginationData;
}

// Define the type returned by adaptProduct to match what's expected by ProductCard
interface ProductBase {
  id: number;
  name: string;
  description: string | null;
  price: string | null;
  price_ars: string | null;
  pricingStrategy?: 'single' | 'per_package';
  imageData: string | null;
  imageMimeType: string | null;
  category: string | null;
  peso: string | null;
  unidad: string | null;
  estado: string | null;
  isQuotable: boolean | null;
  isSellable: boolean | null;
  specifications: Record<string, unknown>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// Adapter function to convert Product to ProductBase format expected by ProductCard
const adaptProduct = (product: Product): ProductBase => {
  return {
    id: product.id,
    name: product.name,
    description: product.description,
    price: product.price !== null ? String(product.price) : null,
    price_ars: product.price_ars !== null && product.price_ars !== undefined ? String(product.price_ars) : null,
    pricingStrategy: product.pricingStrategy,
    imageData: product.imageData || null,
    imageMimeType: product.imageMimeType || null,
    category: product.category,
    peso: product.peso || null,
    unidad: product.unidad || null,
    estado: product.estado || null,
    isQuotable: product.isQuotable ?? true,
    isSellable: product.isSellable ?? false,
    specifications: (product.specifications as Record<string, unknown>) || {},
    isActive: product.isActive ?? true,
    createdAt: product.createdAt ? new Date(product.createdAt) : new Date(),
    updatedAt: product.updatedAt ? new Date(product.updatedAt) : new Date(),
  };
};

export default function ProductSelection() {
  const { addItem } = useQuotation();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  // Initialize filter from URL params
  const [filter, setFilter] = useState<{
    category?: string;
    search?: string;
  }>({
    category: searchParams.get('category') || undefined,
    search: searchParams.get('search') || undefined,
  });
  
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(
    searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1
  );
  const [totalPages, setTotalPages] = useState(1);
  const [quantity, setQuantity] = useState<Record<number, number>>({});

  // Fetch products via API with debounce for search only
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const fetchProducts = async () => {
      setLoading(true);
      try {
        // Build query string from filter and pagination
        const params = new URLSearchParams();
        if (filter.category) params.append('category', filter.category);
        if (filter.search) params.append('search', filter.search);
        params.append('page', currentPage.toString());
        params.append('pageSize', '12');
        // Add filter to only show quotable products and exclude non-quotable
        params.append('isQuotable', 'true');
        // Include packages for better performance
        params.append('includePackages', 'true');
        
        const response = await fetch(`/api/products?${params.toString()}`);
        if (!response.ok) throw new Error('Failed to fetch products');
        
        const data: ProductsResponse = await response.json();
        setProducts(data.data);
        setTotalPages(data.pagination.totalPages);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    // If search is being used, debounce the API call to prevent excessive requests
    if (filter.search && filter.search.length > 0) {
      timeoutId = setTimeout(fetchProducts, 300);
    } else {
      // For category changes and pagination, fetch immediately
      fetchProducts();
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [filter, currentPage]);
  
  // Fetch categories via API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/products', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            isQuotable: true,
          }),
        });
        if (!response.ok) throw new Error('Failed to fetch categories');

        const data = await response.json();
        // Sort categories alphabetically
        const sortedCategories = (data.categories || []).sort();
        setCategories(sortedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);
  
  // Initialize quantity state
  useEffect(() => {
    const initialQuantities: Record<number, number> = {};
    products.forEach(product => {
      initialQuantities[product.id] = 1;
    });
    setQuantity(initialQuantities);
  }, [products]);
  
  // Update URL when filter or pagination changes (instant response)
  useEffect(() => {
    const params = new URLSearchParams();
    if (filter.category) params.set('category', filter.category);
    if (filter.search) params.set('search', filter.search);
    if (currentPage > 1) params.set('page', currentPage.toString());
    
    const queryString = params.toString();
    const newUrl = queryString ? `/cotizar?${queryString}` : '/cotizar';
    router.replace(newUrl, { scroll: false });
  }, [filter, currentPage, router]);
  
  // Handle category filter change
  const handleCategoryChange = (category: string | undefined) => {
    setFilter(prev => ({ ...prev, category }));
    setCurrentPage(1);
  };
  
  // Handle search input
  const handleSearch = (search: string) => {
    setFilter(prev => ({ ...prev, search: search || undefined }));
    setCurrentPage(1);
  };
  
  // Handle adding product to quotation
  const handleAddToQuotation = (product: Product) => {
    console.error('[ProductSelection] OLD QUOTATION HANDLER - This should not be used! Product:', product.name);
    // This function should not be used - ProductCard handles quotations with package data
    // addItem(adaptProduct(product), quantity[product.id] || 1);
  };
  
  // Handle quantity change
  const handleQuantityChange = (productId: number, value: number) => {
    setQuantity(prev => ({
      ...prev,
      [productId]: value
    }));
  };

  // Clear filters while preserving isQuotable for quotable page
  const clearFilters = () => {
    setFilter({});
    setCurrentPage(1);
    router.replace('/cotizar', { scroll: false });
  };
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Solicitar Cotización</h1>
        <p className="text-gray-600 mb-4">
          Busca y selecciona los productos para los que deseas solicitar una cotización.
        </p>
      </div>
      
      {/* Filters and Search Layout */}
      <div className="mb-6">
        {/* Desktop Top Bar */}
        <div className="hidden md:flex items-center gap-4 mb-6 w-full">
          {/* Quick Filters */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm font-medium text-gray-700">Filtros:</span>
            {/* Category Dropdown */}
            <select
              value={filter.category || ''}
              onChange={(e) => handleCategoryChange(e.target.value || undefined)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white focus:ring-1 focus:ring-brand-primary focus:border-brand-primary"
            >
              <option value="">Todas las categorías</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>

            {/* Clear Filters */}
            {(filter.category || filter.search) && (
              <button
                onClick={clearFilters}
                className="text-sm text-brand-primary hover:text-brand-primary-dark flex items-center gap-1 px-2 py-1"
              >
                <X size={14} />
                Limpiar
              </button>
            )}
          </div>

          {/* Search Bar - Right aligned */}
          <div className="flex-1 flex justify-end">
            <SearchBar
              value={filter.search || ''}
              placeholder="Buscar productos para cotizar..."
              onSearch={(query) => handleSearch(query)}
              loading={loading}
              showResults={true}
              resultCount={products.length > 0 ? products.length : undefined}
              className="w-full max-w-md"
            />
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <SearchBar
            value={filter.search || ''}
            placeholder="Buscar productos para cotizar..."
            onSearch={(query) => handleSearch(query)}
            loading={loading}
            showResults={true}
            resultCount={products.length > 0 ? products.length : undefined}
            className="w-full mb-4"
          />
        </div>
      </div>
      
      {/* Mobile Filter Sidebar */}
      <div className="md:hidden mb-6">
        <button
          onClick={() => setShowMobileFilters(!showMobileFilters)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-md shadow-sm hover:bg-brand-primary-dark transition-colors mb-4"
        >
          <Filter size={16} />
          {showMobileFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
        </button>
        
        {showMobileFilters && (
          <div className="bg-white p-4 rounded-lg shadow-md border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center">
                <Filter className="h-4 w-4 mr-2 text-brand-primary" />
                <h2 className="text-base font-semibold">Filtros</h2>
              </div>
              {(filter.category || filter.search) && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-brand-primary hover:text-brand-primary-dark flex items-center gap-1"
                >
                  <X size={12} />
                  Limpiar
                </button>
              )}
            </div>
            
            <div>
              <label className="block text-xs font-medium mb-1">
                Categoría
              </label>
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                <div className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                  <input
                    type="radio"
                    id="all-categories-mobile"
                    name="category-mobile"
                    checked={!filter.category}
                    onChange={() => handleCategoryChange(undefined)}
                    className="mr-1.5 w-3.5 h-3.5"
                  />
                  <label htmlFor="all-categories-mobile" className="text-xs flex-1 cursor-pointer">
                    Todas las categorías
                  </label>
                </div>
                
                {categories.map(category => (
                  <div key={category} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="radio"
                      id={`category-mobile-${category}`}
                      name="category-mobile"
                      checked={filter.category === category}
                      onChange={() => handleCategoryChange(category)}
                      className="mr-1.5 w-3.5 h-3.5"
                    />
                    <label 
                      htmlFor={`category-mobile-${category}`} 
                      className="text-xs flex-1 cursor-pointer"
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Product grid */}
      <div className="w-full">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary"></div>
          </div>
        ) : (
          <>
            {products.length === 0 ? (
              <div className="bg-white p-6 rounded-lg shadow-md text-center">
                <div className="mx-auto mb-6 w-32 h-32 flex items-center justify-center">
                  <svg
                    className="w-24 h-24 text-gray-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No se encontraron productos</h3>
                <p className="text-gray-600 mb-6">Intenta con diferentes criterios de búsqueda o filtros.</p>
                {(filter.category || filter.search) && (
                  <button 
                    onClick={clearFilters} 
                    className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary-dark transition-colors"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {products
                  .filter(product => product.isQuotable !== false)
                  .map((product) => (
                    <ProductCard
                      key={product.id}
                      product={adaptProduct(product)}
                      mode="quotation"
                      previewPackages={product.packages || []}
                      skipPackageFetch={true}
                    />
                  ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8">
                <nav className="flex items-center space-x-2">
                  <button
                    className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  >
                    Anterior
                  </button>
                  
                  <span className="px-3 py-1">
                    Página {currentPage} de {totalPages}
                  </span>
                  
                  <button
                    className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  >
                    Siguiente
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}